<!-- This example requires Tailwind CSS v2.0+ -->
<template>
  <div class="bg-white">
    <div class="">
      <div role="list" class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div class="group col-span-1 flex shadow-sm rounded-xl cursor-pointer border border-solid  hover:rounded-xl hover:bg-gradient-to-tr hover:to-brand-primary-light hover:from-brand-primary-dark" :class="[ hardwareProductAdded ? 'border-none bg-gradient-to-tr to-brand-primary-light from-brand-primary-dark shadow-gray-400' : '' ]">
          <div
            @click="handleClickAddHardwareProduct"
            class="transition duration-250 relative flex-1 flex items-center justify-between "
            :class="[(hardwareProductAdded ? 'border border-black' : 'border border-gray-200'), ('bg-transparent rounded-2xl truncate')]"
            >
            <div class="flex-1 px-8 py-2 text-lg truncate">
              <p class="text-2xl transition duration-250 text-black font-poppins-bold" :class="[hardwareProductAdded ? 'text-white' : 'text-black']">Yes</p>
            </div>
            <div class="flex-shrink-0 pl-4 pr-4 flex items-start justify-start ">
              <button v-if="!hardwareProductAdded" type="button" class="text-right mr-2 w-7 h-7 text-white rounded-full border border-solid"
                :class="[ hardwareProductAdded ? 'text-black' : 'text-gray-600 group-hover:text-gray-400' ]"
                >
              </button>
              <CheckCircleSolidIcon v-if="hardwareProductAdded" class="text-right pr-2 w-8 h-8 text-white" aria-hidden="true" />
            </div>
          </div>
        </div>
        <div class="group col-span-1 flex shadow-sm rounded-xl cursor-pointer border border-solid  hover:rounded-xl hover:bg-gradient-to-tr hover:to-brand-primary-light hover:from-brand-primary-dark" :class="[ !hardwareProductAdded ? 'border-none bg-gradient-to-tr to-brand-primary-light from-brand-primary-dark shadow-gray-400' : '' ]">
          <div
            @click="handleClickRemoveHardwareProduct"
            class="transition duration-250 relative flex-1 flex items-center justify-between"
            :class="[(!hardwareProductAdded ? 'border border-black' : 'border border-gray-200'), ('bg-transparent rounded-2xl truncate')]"
            >
            <div class="flex-1 px-8 py-2 text-lg truncate">
              <p class="text-2xl transition duration-250 text-black font-poppins-bold" :class="[hardwareProductAdded ? 'text-black' : 'text-white']">No</p>
            </div>
            <div class="flex-shrink-0 pl-4 pr-4 flex items-start justify-start ">
              <button v-if="hardwareProductAdded" type="button" class="text-right mr-2 w-7 h-7 text-white rounded-full border border-solid"
                :class="[ !hardwareProductAdded ? 'text-black' : 'text-gray-600 group-hover:text-gray-400' ]"
                >
              </button>
              <CheckCircleSolidIcon v-if="!hardwareProductAdded" class="text-right pr-2 w-8 h-8 text-white" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
      <div v-if="hardwareProductAdded">
        <div class="pt-6 mt-4 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-4">
          <HardwareSelectorProduct
            v-for="group in groupedProducts"
            @handle-select-product="handleSelectProduct"
            :variants="group.variants"
            :key="group.baseName"
            :isSelectedProduct="isGroupSelected(group)"
            :selected="selected"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang='ts'>

import { defineProps, defineEmits, ref, computed, watch } from 'vue';
import HardwareSelectorProduct from './HardwareSelectorProduct.vue';
import { CheckCircleIcon } from '@heroicons/vue/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/vue/solid';

type Product = {
  id: string,
  name: string,
  description: string,
  price: number
}

type ProductVariant = {
  units: number,
  product: Product
}

type ProductGroup = {
  baseName: string,
  variants: Array<ProductVariant>
}

const props = defineProps<{
  products: Array<Product>,
  isHardwareAdded: boolean,
  selected: string
}>();

const emit = defineEmits<{(e: 'onHardwareSelected', id: string): void, (e: 'onHardwareToggled', isVoipAdded: boolean): void}>();

// Start from the parent's state so a restored/loaded router shows as "Yes".
const hardwareProductAdded = ref(props.isHardwareAdded);
watch(() => props.isHardwareAdded, (isHardwareAdded) => {
  hardwareProductAdded.value = isHardwareAdded;
});
const productSelected = ref<Product|null>();

// Bundle products are named e.g. "eero 7 Bundle 1", "eero 7 Bundle 2" and
// represent the same product sold in packs of 2, 3, etc. units. Group them
// under their base product name so they can be shown as unit-count options
// within a single product card, rather than as separate cards.
const parseBundle = (name: string): { baseName: string, units: number } => {
  const match = name.match(/^(.*) Bundle (\d+)$/);
  if (match) {
    return { baseName: match[1], units: parseInt(match[2], 10) + 1 };
  }
  return { baseName: name, units: 1 };
};

const groupedProducts = computed<Array<ProductGroup>>(() => {
  const groups: Array<ProductGroup> = [];
  const groupsByName = new Map<string, ProductGroup>();

  for (const product of props.products) {
    const { baseName, units } = parseBundle(product.name);
    let group = groupsByName.get(baseName);
    if (!group) {
      group = { baseName, variants: [] };
      groupsByName.set(baseName, group);
      groups.push(group);
    }
    group.variants.push({ units, product });
  }

  for (const group of groups) {
    group.variants.sort((a, b) => a.units - b.units);
  }

  return groups;
});

const handleClickAddHardwareProduct = () => {
  hardwareProductAdded.value = true;
  emit('onHardwareToggled', true);
};

const handleClickRemoveHardwareProduct = () => {
  hardwareProductAdded.value = false;
  emit('onHardwareToggled', false);
};

const handleSelectProduct = (product: Product): void => {
  productSelected.value = product;
  emit('onHardwareSelected', product.id);
};

const isGroupSelected = (group: ProductGroup): boolean => {
  return group.variants.some(variant => variant.product.id === props.selected);
};

</script>
